import React, { useState } from "react";
import { Loader2, Sparkles, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

/**
 * AdminMigrationsCard
 *
 * Two one-tap migrations the admin can run safely on production:
 *  1. "Brighten thread covers" — overwrites legacy dark Unsplash photos
 *     stored in `cover_image` with a bright topic-matched replacement.
 *  2. "De-duplicate campsites" — removes campsites that share
 *     (name + lat~4dp + lng~4dp). Keeps the oldest.
 *
 * Both are idempotent: running them on an already-clean DB is a no-op
 * and returns a count of 0.
 */
export default function AdminMigrationsCard() {
  const [brightenState, setBrightenState] = useState({ phase: "idle", data: null });
  const [dedupeState, setDedupeState] = useState({ phase: "idle", data: null });

  const runBrighten = async () => {
    setBrightenState({ phase: "running", data: null });
    try {
      const { data } = await api.post("/admin/migrations/replace-dark-thread-covers");
      setBrightenState({ phase: "done", data });
      toast.success(`Brightened ${data.updated} of ${data.scanned} threads`);
    } catch (e) {
      setBrightenState({ phase: "failed", data: e?.response?.data?.detail || "Failed" });
      toast.error("Brighten failed");
    }
  };

  const runDedupe = async () => {
    setDedupeState({ phase: "running", data: null });
    try {
      const { data } = await api.post("/admin/migrations/dedupe-campsites");
      setDedupeState({ phase: "done", data });
      toast.success(`Removed ${data.removed} duplicate(s) · ${data.total_after.toLocaleString()} total`);
    } catch (e) {
      setDedupeState({ phase: "failed", data: e?.response?.data?.detail || "Failed" });
      toast.error("De-dupe failed");
    }
  };

  return (
    <div className="mb-10 p-5 border hairline bg-white" data-testid="admin-migrations-card">
      <div className="eyebrow mb-1">One-tap migrations</div>
      <div className="font-display text-xl mb-1">Clean up the catalogue</div>
      <p className="text-sm text-[#555] mb-5">
        Two safe, idempotent migrations. Re-running them on an already-clean
        DB is a no-op (returns count 0). Run from production once after the
        next redeploy.
      </p>

      <div className="grid sm:grid-cols-2 gap-4">
        {/* 1. Brighten thread covers */}
        <div className="p-4 border hairline bg-[#fafafa]" data-testid="brighten-block">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-[#ff4500]" strokeWidth={1.75} />
            <div className="font-medium text-sm">Brighten thread covers</div>
          </div>
          <p className="text-xs text-[#666] mb-3">
            Replace any legacy admin-seeded dark Unsplash photos in
            <code className="mono mx-1 px-1 bg-white">threads.cover_image</code>
            with a bright topic-matched picture.
          </p>
          <button
            onClick={runBrighten}
            disabled={brightenState.phase === "running"}
            className="btn-primary text-xs inline-flex items-center gap-2"
            data-testid="brighten-btn"
          >
            {brightenState.phase === "running"
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Brightening…</>
              : "Run brighten"}
          </button>
          {brightenState.phase === "done" && (
            <div className="mt-3 text-xs text-emerald-700 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Updated {brightenState.data.updated} of {brightenState.data.scanned} threads
            </div>
          )}
          {brightenState.phase === "failed" && (
            <div className="mt-3 text-xs text-[#ff4500] flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> {String(brightenState.data)}
            </div>
          )}
        </div>

        {/* 2. De-dupe campsites */}
        <div className="p-4 border hairline bg-[#fafafa]" data-testid="dedupe-block">
          <div className="flex items-center gap-2 mb-2">
            <Trash2 className="w-4 h-4 text-[#ff4500]" strokeWidth={1.75} />
            <div className="font-medium text-sm">De-duplicate campsites</div>
          </div>
          <p className="text-xs text-[#666] mb-3">
            Remove campsites sharing the same name + coordinates (rounded to
            ~11&nbsp;m). Keeps the oldest record so historical favourites &
            shared links stay valid.
          </p>
          <button
            onClick={runDedupe}
            disabled={dedupeState.phase === "running"}
            className="btn-primary text-xs inline-flex items-center gap-2"
            data-testid="dedupe-btn"
          >
            {dedupeState.phase === "running"
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> De-duping…</>
              : "Run de-dupe"}
          </button>
          {dedupeState.phase === "done" && (
            <div className="mt-3 text-xs text-emerald-700">
              <div className="flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Removed {dedupeState.data.removed} duplicate(s)
              </div>
              <div className="mono mt-1 text-[#555]">
                {dedupeState.data.total_after.toLocaleString()} unique sites
              </div>
              {dedupeState.data.samples?.length > 0 && (
                <ul className="mt-2 list-disc pl-4 text-[#555]">
                  {dedupeState.data.samples.slice(0, 4).map((s, i) => (
                    <li key={i}>{s.name} ({s.removed_count}×)</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {dedupeState.phase === "failed" && (
            <div className="mt-3 text-xs text-[#ff4500] flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> {String(dedupeState.data)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
